import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { QuizSubject } from '../../../interfaces/question.interface';
import { GamePhase } from '../../../interfaces/game-state.interface';
import { GameModeService } from '../../../_services/game-mode.service';
import { MiniGameService } from '../../../_services/mini-game.service';
import { GlobalService } from 'src/app/_services/global.service';
import { ACADEMIC_GAMES, AcademicGame } from '../../../data/academic-games';
import { requestBrowserFullscreen } from '../../mini-games/_shared/immersive-game.base';

/**
 * The skill-games shelf for one subject inside "Academic Skill Games".
 * Reads the subject chosen on the previous screen
 * (`MiniGameService.getSelectedSubject`) and renders its games from the
 * shared `ACADEMIC_GAMES` map. Math currently has Mirror Match + Star
 * Match; a subject with no games yet shows a friendly empty state.
 */
@Component({
  selector: 'app-academic-games-picker',
  templateUrl: './academic-games-picker.component.html',
  styleUrls: ['./academic-games-picker.component.scss']
})
export class AcademicGamesPickerComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  currLang: any = 'en';

  subject: QuizSubject | 'random' = QuizSubject.MATH;
  games: AcademicGame[] = [];

  constructor(
    private gameModeService: GameModeService,
    private miniGameService: MiniGameService,
    private globalService: GlobalService
  ) {}

  ngOnInit(): void {
    this.globalService.getLang()
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => this.currLang = lang);

    this.subject = this.miniGameService.getSelectedSubject();
    this.games = (this.subject !== 'random' && ACADEMIC_GAMES[this.subject]) || [];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get subjectKey(): string {
    return this.subject === 'random' ? 'general' : this.subject;
  }

  play(game: AcademicGame): void {
    // This runs inside the card's click gesture, so the fullscreen request is
    // honoured — games open fullscreen. Harmless (no-op) if already fullscreen.
    requestBrowserFullscreen();
    if (game.launch === 'symmetry') {
      this.miniGameService.startSymmetryGame();
    } else if (game.launch === 'matchup') {
      this.miniGameService.startMatchUpGame();
    } else if (game.launch === 'fractionline') {
      this.miniGameService.startFractionLineGame();
    } else if (game.launch === 'fractionhops') {
      this.miniGameService.startFractionHopsGame();
    } else if (game.launch === 'fractiontrek') {
      this.miniGameService.startFractionTrekGame();
    } else if (game.launch === 'zoomquest') {
      this.miniGameService.startZoomQuestGame();
    } else if (game.launch === 'treasuregrid') {
      this.miniGameService.startTreasureGridGame();
    } else if (game.launch === 'cubefill') {
      this.miniGameService.startCubeFillGame();
    } else if (game.launch === 'balanceit') {
      this.miniGameService.startBalanceItGame();
    } else if (game.launch === 'powerburst') {
      this.miniGameService.startPowerBurstGame();
    } else if (game.launch === 'cuttingpizza') {
      this.miniGameService.startCuttingPizzaGame();
    } else if (game.launch === 'vaultcracker') {
      this.miniGameService.startVaultCrackerGame();
    }
  }

  goBack(): void {
    this.gameModeService.setPhase(GamePhase.ACADEMIC_SUBJECT_PICK);
  }
}
